import React, { useState, useRef, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FaChevronDown } from 'react-icons/fa';
import StyledMainMenuUniversal from './MainMenuUniversal.styled';
import { featuredBrandPT, categoriesPT, personalityPT, discoversAllPT } from '../../../config/prop-types';
import { locationActions } from '../../../actions';
import { ThemePramsContext } from '../../../App';

const defaultChevron = (props) => <FaChevronDown {...props} />;

// Builds a url out of a config pattern, e.g. '/category/{uri}' or '/shop?category={uri}'
const buildUrl = (pattern, uri) => (pattern || '').replace('{uri}', uri);

/**
 * One desktop navigation for every marketplace. The markup is the rebud one
 * (MainMenuRebud); the sections, labels, urls, icons and fonts come from
 * headerConfig.desktopNavConfig of the selected market config.
 */
const MainMenuUniversal = React.memo(
  ({ categories, featuredBrand, personality, discoversAll, setMenuOpen, seoEnabled, seoReady, menuAvailability }) => {
  const [active, setActive] = useState(null);
  const closeTimer = useRef(null);
  const itemRefs = useRef({});
  const [dispensaries, setDispensaries] = useState([]);

  const { headerConfig = {} } = useContext(ThemePramsContext);
  const navConfig = headerConfig.desktopNavConfig || {};
  const { classNames = {}, thc = [], shop = {}, items = [], urls = {}, styling = {} } = navConfig;
  const Chevron = navConfig.chevron || defaultChevron;

  // Stores with seo turned off get the short menu: items marked seoOnly are hidden
  const seoOff = seoEnabled === false;
  const hideDispensaries = menuAvailability?.dispensaries === false;
  const hideDelivery = menuAvailability?.deliveryAreas === false;
  const visibleItems = items.filter(
    (item) =>
      item.visible !== false &&
      !(seoOff && item.seoOnly) &&
      !(hideDispensaries && (item.type === 'dispensaries' || item.key === 'dispensaries')) &&
      !(hideDelivery && item.key === 'delivery')
  );

  const needsDispensaries = visibleItems.some((item) => item.type === 'dispensaries');

  useEffect(() => {
    if (!needsDispensaries) return;
    locationActions
      .getPickupLocationsList()
      .then((data) => {
        if (data?.locations?.length > 0) {
          setDispensaries(data.locations.map((item) => ({ uri_param: item.uri_param, name: item.name })));
        }
      })
      .catch(() => {});
  }, [needsDispensaries]); // eslint-disable-line react-hooks/exhaustive-deps

  const allBrands = featuredBrand?.[0]?.brands || [];
  const brandCols = (() => {
    if (!allBrands.length) return [];
    const numCols = 4;
    const perCol = Math.ceil(allBrands.length / numCols);
    return Array.from({ length: numCols }, (_, i) => allBrands.slice(i * perCol, (i + 1) * perCol)).filter(
      (col) => col.length > 0
    );
  })();

  const open = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(key);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActive(null), 150);
  };

  const handleLinkClick = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActive(null);
    setMenuOpen && setMenuOpen(false);
  };

  const getDropdownStyle = (key) => {
    const el = itemRefs.current[key];
    if (!el || typeof window === 'undefined') return { left: 0 };
    const rect = el.getBoundingClientRect();
    const spaceToLeft = rect.left + 8;
    return { maxWidth: `calc(100vw - ${spaceToLeft}px)` };
  };

  const linkClass = `rmenu__link ${classNames.link || ''}`;
  const headingClass = `rmenu__col-heading ${classNames.heading || ''}`;
  const dropdownItemClass = `rmenu__dropdown-item ${classNames.dropdownItem || classNames.link || ''}`;
  const triggerClass = (key) =>
    `rmenu__trigger ${classNames.trigger || ''}${active === key ? ' rmenu__trigger--open' : ''}`;

  const renderColumn = (column, list, type) => {
    if (!column?.visible || !list || !list.length) return null;
    return (
      <div className="rmenu__col" key={type}>
        <h3 className={headingClass}>{column.heading}</h3>
        <ul className="rmenu__col-list">
          {list.map((item, i) => (
            <li key={i}>
              <Link to={buildUrl(urls[type], item.uri_param)} onClick={handleLinkClick} className={linkClass}>
                {item.name}
              </Link>
            </li>
          ))}
          {type === 'category' && shop.shopAll && (
            <li>
              <Link
                to={shop.shopAll.to || '/shop'}
                onClick={handleLinkClick}
                className={`${linkClass} rmenu__link--shop-all ${classNames.shopAll || ''}`}
              >
                {shop.shopAll.text}
              </Link>
            </li>
          )}
        </ul>
      </div>
    );
  };

  const renderShopItem = (item) => (
    <li
      key={item.key}
      ref={(el) => (itemRefs.current[item.key] = el)}
      className="rmenu__item rmenu__item--shop"
      onMouseEnter={() => open(item.key)}
      onMouseLeave={scheduleClose}
    >
      <button className={triggerClass(item.key)} type="button">
        {item.text} <Chevron className="rmenu__chevron" />
      </button>

      {active === item.key && (
        <div className="rmenu__mega" style={getDropdownStyle(item.key)}>
          <div className="rmenu__mega-inner">
            {renderColumn(shop.category, categories?.slice(0, 9), 'category')}
            {renderColumn(shop.personality, personality, 'by_personality')}
            {renderColumn(shop.potency, thc, 'thc')}
            {renderColumn(shop.discover, discoversAll, 'discover')}
          </div>
        </div>
      )}
    </li>
  );

  const renderBrandsItem = (item) => {
    if (!allBrands.length) return null;
    return (
      <li
        key={item.key}
        ref={(el) => (itemRefs.current[item.key] = el)}
        className="rmenu__item rmenu__item--dropdown"
        onMouseEnter={() => open(item.key)}
        onMouseLeave={scheduleClose}
      >
        <button className={triggerClass(item.key)} type="button">
          {item.text} <Chevron className="rmenu__chevron" />
        </button>
        {active === item.key && (
          <div className="rmenu__mega" style={getDropdownStyle(item.key)}>
            <div className="rmenu__mega-inner">
              {brandCols.map((col, ci) => (
                <div key={ci} className="rmenu__col">
                  <ul className="rmenu__col-list">
                    {col.map((brand, i) => (
                      <li key={i}>
                        <Link
                          to={buildUrl(urls.brand, brand.uri_param)}
                          onClick={handleLinkClick}
                          className={linkClass}
                        >
                          {brand.name}
                        </Link>
                      </li>
                    ))}
                    {ci === 0 && item.allLink && (
                      <li>
                        <Link
                          to={item.allLink.to}
                          onClick={handleLinkClick}
                          className={`${linkClass} rmenu__link--shop-all ${classNames.shopAll || ''}`}
                        >
                          {item.allLink.text}
                        </Link>
                      </li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}
      </li>
    );
  };

  const renderDispensariesItem = (item) => (
    <li
      key={item.key}
      ref={(el) => (itemRefs.current[item.key] = el)}
      className="rmenu__item rmenu__item--dropdown"
      onMouseEnter={() => open(item.key)}
      onMouseLeave={scheduleClose}
    >
      <button className={triggerClass(item.key)} type="button">
        {item.text} <Chevron className="rmenu__chevron" />
      </button>
      {active === item.key && dispensaries.length > 0 && (
        <div className="rmenu__dropdown" style={getDropdownStyle(item.key)}>
          {dispensaries.map((loc, i) => (
            <Link
              key={i}
              to={buildUrl(urls.dispensary, loc.uri_param)}
              onClick={handleLinkClick}
              className={dropdownItemClass}
            >
              {loc.name}
            </Link>
          ))}
        </div>
      )}
    </li>
  );

  const renderDropdownItem = (item) => (
    <li
      key={item.key}
      ref={(el) => (itemRefs.current[item.key] = el)}
      className="rmenu__item rmenu__item--dropdown"
      onMouseEnter={() => open(item.key)}
      onMouseLeave={scheduleClose}
    >
      <button className={triggerClass(item.key)} type="button">
        {item.text} <Chevron className="rmenu__chevron" />
      </button>
      {active === item.key && (
        <div className="rmenu__dropdown" style={getDropdownStyle(item.key)}>
          {(item.links || [])
            .filter((link) => !(seoOff && link.seoOnly))
            .map((link, i) =>
            link.href ? (
              <a key={i} href={link.href} onClick={handleLinkClick} className={dropdownItemClass}>
                {link.name}
              </a>
              ) : (
                <Link key={i} to={link.to} onClick={handleLinkClick} className={dropdownItemClass}>
                  {link.name}
                </Link>
              )
            )}
        </div>
      )}
    </li>
  );

  const renderLinkItem = (item) => (
    <li className="rmenu__item" key={item.key}>
      {item.href ? (
        <a href={item.href} className={`${triggerClass(item.key)} rmenu__trigger--link`} onClick={handleLinkClick}>
          {item.text}
        </a>
      ) : (
        <Link to={item.to} className={`${triggerClass(item.key)} rmenu__trigger--link`} onClick={handleLinkClick}>
          {item.text}
        </Link>
      )}
    </li>
  );

  const renderItem = (item) => {
    switch (item.type) {
      case 'shop':
        return renderShopItem(item);
      case 'brands':
        return renderBrandsItem(item);
      case 'dispensaries':
        return renderDispensariesItem(item);
      case 'dropdown':
        return renderDropdownItem(item);
      default:
        return renderLinkItem(item);
    }
  };

  // Wait for /stores/info: rendering the full menu first and trimming it once
  // the flag arrives makes the items visibly disappear
  if (!seoReady || !visibleItems.length) return null;

  return (
    <StyledMainMenuUniversal themeProps={styling}>
      <nav className="rmenu" aria-label="Main navigation">
        <ul className="rmenu__list">{visibleItems.map(renderItem)}</ul>
      </nav>
    </StyledMainMenuUniversal>
  );
});

MainMenuUniversal.propTypes = {
  categories: categoriesPT,
  featuredBrand: featuredBrandPT,
  personality: personalityPT,
  discoversAll: discoversAllPT,
  setMenuOpen: PropTypes.func,
  seoEnabled: PropTypes.bool,
  seoReady: PropTypes.bool,
  menuAvailability: PropTypes.object,
};

export default MainMenuUniversal;
